import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SportsRoundedIcon from '@mui/icons-material/SportsRounded';
import MilitaryTechRoundedIcon from '@mui/icons-material/MilitaryTechRounded';
import InstallMobileRoundedIcon from '@mui/icons-material/InstallMobileRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import Badge from '@mui/material/Badge';
import { useNotificacoes } from '../utils/notificacoes';
import InstallAppDialog from './InstallAppDialog';
import { assinar, podeInstalar } from '../utils/pwa';
import { useAuth } from '../AuthContext';
import PlayerAvatar from './ui/PlayerAvatar';
import tokens from '../theme/tokens';

const publicLinks = [
  { to: '/', label: 'Início', icon: <HomeRoundedIcon /> },
  { to: '/squad', label: 'Torneio', icon: <EmojiEventsRoundedIcon /> },
  { to: '/estatuto', label: 'Regras', icon: <GavelRoundedIcon /> },
];

const memberLinks = [
  { to: '/painel', label: 'Painel', icon: <DashboardRoundedIcon /> },
  { to: '/ordem-chegada', label: 'Confirmação / Ordem de chegada', short: 'Confirmação', icon: <PlaceRoundedIcon /> },
  { to: '/avaliacao', label: 'Avaliação da pelada', icon: <StarRoundedIcon /> },
  { to: '/financeiro', label: 'Financeiro', icon: <AccountBalanceWalletRoundedIcon /> },
  { to: '/torneio/painel', label: 'Painel do torneio', icon: <MilitaryTechRoundedIcon /> },
  { to: '/votacao', label: 'Votação dos potes', icon: <HowToVoteRoundedIcon /> },
  { to: '/ranking', label: 'Eficiência', icon: <InsightsRoundedIcon /> },
];

const arbitroLinks = [{ to: '/arbitro', label: 'Partidas', icon: <SportsRoundedIcon /> }];
const goleiroLinks = [
  { to: '/goleiro', label: 'Check-in', icon: <SportsRoundedIcon /> },
  { to: '/avaliacao', label: 'Avaliação', icon: <StarRoundedIcon /> },
];

const adminLinks = [
  { to: '/user/aprovacao', label: 'Administração', icon: <AdminPanelSettingsRoundedIcon /> },
  { to: '/peladas', label: 'Peladas', icon: <EventNoteRoundedIcon /> },
  { to: '/usuarios/novo', label: 'Cadastrar usuário', icon: <PersonAddAlt1RoundedIcon /> },
  { to: '/arbitro', label: 'Arbitragem', icon: <SportsRoundedIcon /> },
  { to: '/adminJogadores', label: 'Jogadores do torneio', icon: <GroupsRoundedIcon /> },
  { to: '/torneio', label: 'Jogos do torneio', icon: <SportsSoccerRoundedIcon /> },
];

export const Brand = ({ light = true }) => (
  <Stack component={RouterLink} to="/" direction="row" spacing={1.25} alignItems="center" sx={{ textDecoration: 'none' }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#fff', display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
      <Box component="img" src="/logo_rfc.png" alt="Riviera FC" sx={{ width: 36, height: 36, objectFit: 'contain' }} />
    </Box>
    <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '1.4rem', letterSpacing: '0.02em', color: light ? '#fff' : tokens.color.text }}>
      RIVIERA <Box component="span" sx={{ color: tokens.color.brandLight }}>FC</Box>
    </Typography>
  </Stack>
);

const Header = () => {
  const { isAuthenticated, logout, userLogado } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [instalavel, setInstalavel] = useState(podeInstalar());
  const [instalarAberto, setInstalarAberto] = useState(false);

  React.useEffect(() => assinar(() => setInstalavel(podeInstalar())), []);

  const { naoLidas } = useNotificacoes();
  const isManager = userLogado && (userLogado.perfil === 'Admin' || userLogado.perfil === 'Diretor');
  const isGoleiro = isAuthenticated && userLogado?.perfil === 'Goleiro';
  const isArbitro = (isAuthenticated && userLogado?.perfil === 'Arbitro') || isGoleiro; // perfis da equipe: menu restrito
  const equipeLinks = isGoleiro ? goleiroLinks : arbitroLinks;
  const topLinks = isArbitro ? equipeLinks : isAuthenticated ? [memberLinks[0], memberLinks[1], memberLinks[2], publicLinks[1]] : publicLinks;
  const menuLinks = isArbitro ? equipeLinks : memberLinks;

  const go = (to) => {
    setAnchorEl(null);
    setDrawerOpen(false);
    navigate(to);
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    setDrawerOpen(false);
    await logout();
    navigate('/');
  };

  const renderGroup = (title, links) => (
    <List subheader={<ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '36px' }}>{title}</ListSubheader>} dense>
      {links.map((link) => (
        <ListItemButton key={link.to} selected={pathname === link.to} onClick={() => go(link.to)} sx={{ borderRadius: 2, mx: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>{link.icon}</ListItemIcon>
          <ListItemText primary={link.label} />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: tokens.color.ink, borderBottom: `1px solid ${tokens.color.inkSoft}` }}>
      <Toolbar sx={{ minHeight: tokens.layout.headerHeight, maxWidth: tokens.layout.maxWidth, width: '100%', mx: 'auto', px: { xs: 2, sm: 3 } }}>
        <IconButton edge="start" color="inherit" aria-label="Abrir menu" onClick={() => setDrawerOpen(true)} sx={{ mr: 1, display: { md: 'none' } }}>
          <MenuRoundedIcon />
        </IconButton>
        <Brand />

        <Stack direction="row" spacing={0.5} sx={{ ml: 4, flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
          {topLinks.map((link) => (
            <Button
              key={link.to}
              onClick={() => navigate(link.to)}
              sx={{
                color: pathname === link.to ? '#fff' : 'rgba(255,255,255,0.72)',
                bgcolor: pathname === link.to ? 'rgba(255,255,255,0.08)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' },
              }}
            >
              {link.short || link.label}
            </Button>
          ))}
        </Stack>
        <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

        {isAuthenticated ? (
          <>
            {!isArbitro && (
              <IconButton color="inherit" aria-label={naoLidas ? `${naoLidas} notificações não lidas` : 'Notificações'} onClick={() => navigate('/notificacoes')} sx={{ mr: 0.5 }}>
                <Badge badgeContent={naoLidas} color="error" max={99}>
                  <NotificationsRoundedIcon />
                </Badge>
              </IconButton>
            )}
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ color: '#fff', gap: 1, pl: 0.5, pr: 1.5, borderRadius: 99, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
              aria-haspopup="true"
            >
              <PlayerAvatar src={userLogado?.foto} name={userLogado?.nome} size={34} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userLogado?.nickName || (userLogado?.nome || '').split(' ')[0]}
              </Box>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1, minWidth: 240 } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography fontWeight={700}>{userLogado?.nome}</Typography>
                <Typography variant="body2" color="text.secondary">{isGoleiro ? 'Goleiro' : isArbitro ? 'Árbitro' : userLogado?.email}</Typography>
              </Box>
              <Divider />
              {!isArbitro && (
                <MenuItem onClick={() => go('/perfil')}>
                  <ListItemIcon><PersonRoundedIcon fontSize="small" /></ListItemIcon>
                  Meu perfil
                </MenuItem>
              )}
              {menuLinks.map((link) => (
                <MenuItem key={link.to} onClick={() => go(link.to)}>
                  <ListItemIcon>{React.cloneElement(link.icon, { fontSize: 'small' })}</ListItemIcon>
                  {link.label}
                </MenuItem>
              ))}
              {isManager && <Divider />}
              {isManager &&
                adminLinks.map((link) => (
                  <MenuItem key={link.to} onClick={() => go(link.to)}>
                    <ListItemIcon>{React.cloneElement(link.icon, { fontSize: 'small' })}</ListItemIcon>
                    {link.label}
                  </MenuItem>
                ))}
              <Divider />
              {instalavel && (
                <MenuItem onClick={() => { setAnchorEl(null); setInstalarAberto(true); }}>
                  <ListItemIcon><InstallMobileRoundedIcon fontSize="small" /></ListItemIcon>
                  Instalar app
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon><LogoutRoundedIcon fontSize="small" color="error" /></ListItemIcon>
                Sair
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button variant="contained" startIcon={<LoginRoundedIcon />} onClick={() => navigate('/login')}>
            Entrar
          </Button>
        )}
      </Toolbar>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 280, borderRadius: 0 } }}>
        <Box sx={{ p: 2, bgcolor: tokens.color.ink }}>
          <Brand />
        </Box>
        {isArbitro ? renderGroup(isGoleiro ? 'Goleiro' : 'Arbitragem', equipeLinks) : renderGroup('Riviera FC', publicLinks)}
        {isAuthenticated && !isArbitro && renderGroup('Pelada', [{ to: '/perfil', label: 'Meu perfil', icon: <PersonRoundedIcon /> }, ...memberLinks])}
        {isAuthenticated && isManager && renderGroup('Administração', adminLinks)}
        <Divider sx={{ my: 1 }} />
        <List dense>
          {instalavel && (
            <ListItemButton onClick={() => { setDrawerOpen(false); setInstalarAberto(true); }} sx={{ borderRadius: 2, mx: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><InstallMobileRoundedIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Instalar app" />
            </ListItemButton>
          )}
          {isAuthenticated ? (
            <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, mx: 1, color: 'error.main' }}>
              <ListItemIcon sx={{ minWidth: 36 }}><LogoutRoundedIcon color="error" /></ListItemIcon>
              <ListItemText primary="Sair" />
            </ListItemButton>
          ) : (
            <ListItemButton onClick={() => go('/login')} sx={{ borderRadius: 2, mx: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><LoginRoundedIcon /></ListItemIcon>
              <ListItemText primary="Entrar" />
            </ListItemButton>
          )}
        </List>
      </Drawer>
      <InstallAppDialog open={instalarAberto} onClose={() => setInstalarAberto(false)} onRecusar={() => setInstalarAberto(false)} />
    </AppBar>
  );
};

export default Header;
